import { useCreateUserPasswordResetLink } from '@vlabs/api/artemy/client';
import { toast } from 'react-toastify';

import { useResetUserPasswordTranslation } from './i18n';
import type { ResetUserPassword } from './types';
import { confirmResetUserPassword } from './ui/confirmation/confirmResetUserPassword';

type UseResetUserPasswordWithConfirmationReturn = () => Promise<void>;

function useResetUserPasswordWithConfirmation({
  userId,
}: ResetUserPassword): UseResetUserPasswordWithConfirmationReturn {
  const { t } = useResetUserPasswordTranslation();
  const { mutateAsync: createUserPasswordResetLink } = useCreateUserPasswordResetLink();

  return async (): Promise<void> => {
    if (!userId) return Promise.reject();

    async function copyUrlToClipboard(url: string): Promise<void> {
      if (!url || typeof navigator === 'undefined' || !navigator.clipboard) return;
      await navigator.clipboard.writeText(url).catch(() => undefined);
      toast.success(t('toast_success'));
    }

    let url: string;

    try {
      const passwordResetLink = await createUserPasswordResetLink({ userId });
      url = passwordResetLink.url;
      await copyUrlToClipboard(url);
    } catch (error) {
      toast.error(t('toast_error'));
      throw error;
    }

    return new Promise<void>((resolve, reject) => {
      confirmResetUserPassword({
        url,
        onConfirm: resolve,
        onReject: reject,
      });
    });
  };
}

export { useResetUserPasswordWithConfirmation };
